<template>
  <div class="" v-if="isPropertyLoading || isTrainingModuleLoading"><Loading /></div>

  <div v-else>
    <BethsLayout :layoutTitle="`${trainingTest?.title} - ${property?.name}`">
      <div v-if="property?.isTrainingEnabled">
        <div v-if="question">
          <h1 class="mt-2 text-xl">{{ trainingTest?.title }}</h1>
          <h2 class="mb-8 font-serif text-base text-gray-400">
            Test Question {{ questionNumber }} of {{ testQuestions?.length }}:
          </h2>
          <p class="font-bold text-2xl mb-12">{{ question.text }}</p>
          <div class="flex m-4 p-1 text-lg" v-for="answer in question.answers">
            <div
              class="my-auto"
              :class="
                answer.answerId === trainingStore.clientAnswers[questionNumber - 1]
                  ? ''
                  : 'invisible'
              "
            >
              <div>
                <IHeroiconsOutlineCheck class="bg-green-200" v-if="answer.isCorrect" />
                <IHeroiconsOutlineX class="bg-red-200" v-else />
              </div>
            </div>
            <span class="ml-2 flex-grow">{{ answer.text }}</span>
          </div>
          <p class="my-8 font-serif text-lg italic text-gray-700">
            {{ question.explanation }}
          </p>

          <button
            class="mt-4 p-4 bg-blue-800 text-gray-50 border rounded-md hover:bg-blue-200 hover:text-gray-900 disabled:bg-gray-300"
            @click="nextClick"
            v-if="questionNumber < (testQuestions?.length || 0)"
          >
            Next Question
          </button>

          <button
            class="mt-4 p-4 bg-blue-800 text-gray-50 border rounded-md hover:bg-blue-200 hover:text-gray-900 disabled:bg-gray-300"
            @click="finishModuleClick"
            v-else-if="property.showFinishButtonAtEndOfQuiz"
          >
            Finish Module
          </button>
          <ErrorDisplay
            label="Your score could not be saved"
            :message="errorSavingMessage"
            :isVisible="errorSavingMessage != ''"
          />
        </div>
        <div v-else>
          <p class="pb-8">Test not found.</p>
          <button
            class="mt-4 p-4 bg-blue-800 text-gray-50 border rounded-md hover:bg-blue-200 hover:text-gray-900 disabled:bg-gray-300"
            @click="exitModule"
          >
            Exit Module
          </button>
        </div>
      </div>
      <div v-else>
        <h1>Training Not Enabled</h1>
      </div>
    </BethsLayout>
  </div>
</template>

<script setup lang="ts">
import { MetricCaptureNames, useMetrics } from "../helpers/metrics";
import { useBldgStore } from "../store/bldgStore";
import {
  SaveResultSelectionDTO,
  TrainingProgramDTO,
} from "../store/generatedFetchClient";
import { useTrainingStore } from "../store/trainingStore";
import { useUserStore } from "../store/userStore";

const router = useRouter();
const route = useRoute();
const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();
const userStore = useUserStore();
const metrics = useMetrics();

var user = computed(() => userStore.user);
var isTrainingModuleLoading = ref(true);
var trainingTest = computed(() => trainingStore.selectedModuleTests?.at(0));
var testQuestions = computed(() => trainingStore.selectedTestQuestions);
var questionNumber = ref(-1);
var question = computed(() =>
  testQuestions.value ? testQuestions.value[questionNumber.value - 1] : null
);

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);

var errorSavingMessage = ref("");

onMounted(loadData);
watch(route, loadData);
async function loadData() {
  isTrainingModuleLoading.value = true;
  redirectIfNotLoggedIn();
  if (route.params.questionNumber && typeof route.params.questionNumber == "string") {
    questionNumber.value = Number.parseInt(route.params.questionNumber);
  }
  if (route.params.moduleId && typeof route.params.moduleId === "string") {
    let trainingModuleId = Number.parseInt(route.params.moduleId);
    await trainingStore.getTestsByTrainingModule(trainingModuleId);
    if (trainingStore.selectedModuleTests?.[0]) {
      await trainingStore.getQuestionsByTest(
        trainingStore.selectedModuleTests?.[0].testId!
      );
    }
  }

  if (
    questionNumber.value === testQuestions.value?.length &&
    !property.value?.showFinishButtonAtEndOfQuiz
  ) {
    finishModuleClick();
  }

  isTrainingModuleLoading.value = false;
}

var isUserLoadedFromServer = computed(() => userStore.isUserLoadedFromServer);
watch(isUserLoadedFromServer, redirectIfNotLoggedIn);
function redirectIfNotLoggedIn() {
  if (property.value && isUserLoadedFromServer.value && !userStore.isLoggedIn)
    router.push(`/${property.value.siteCode}/login?returnUrl=training`);
}

function nextClick() {
  router.push(`./../${questionNumber.value + 1}`);
}

function exitModule() {
  router.push(`../../../../`);
}

async function finishModuleClick() {
  errorSavingMessage.value = "";

  if (!property.value) return;

  let result = {
    personId: userStore.user?.personId,
    testId: trainingTest.value?.testId,
    trainingModuleId: trainingTest.value?.moduleId,
    clientId: bldgStore.building?.clientId,
    selections: trainingStore.clientAnswers.map((answerId, i) => {
      let q = testQuestions.value?.[i];
      let a = q?.answers?.find((q) => q.answerId === answerId);
      return {
        questionId: q?.questionId,
        answerId: a?.answerId,
        score: a?.isCorrect ? 1 : 0,
        questionCopy: q?.text,
        answerCopy: a?.text,
      } as SaveResultSelectionDTO;
    }),
  };

  console.log("Saving module", result);
  errorSavingMessage.value = await trainingStore.saveResult(result);
  if (!errorSavingMessage.value) {
    metrics.capture(MetricCaptureNames.TestCompleted);

    await userStore.getUser(property.value.siteCode!, property.value.portalId!, true);
    var moduleId = trainingTest.value?.moduleId;
    var trainingProgramId = trainingStore.getTrainingProgamId(moduleId!);

    if (trainingProgramId) await trainingStore.getTrainingProgram(trainingProgramId);

    var completedModules = trainingStore.selectedTrainingProgram?.trainingModules?.filter(
      (m) =>
        m.isActive &&
        user.value?.results!.find(
          (r) =>
            r.trainingModuleId == m.trainingModuleId && r.isPassingScore && !r.isExpired
        )
    );

    if (property.value.showFinishButtonAtEndOfQuiz) {
      let modules = (trainingStore.selectedTrainingProgram as TrainingProgramDTO | null)
        ?.trainingModules;
      if (
        modules
          ?.filter((m) => m.isActive)
          .every((m) =>
            completedModules?.find((cm) => cm.trainingModuleId === m.trainingModuleId)
          )
      ) {
        router.push(
          `../../../../${trainingStore.selectedTrainingProgram?.trainingProgramId}/finish`
        );
      } else {
        exitModule();
      }
    }
  } else {
    console.warn("Error saving Module" + errorSavingMessage.value);
  }
}
</script>
